<template>
  <div class="app">

    <!-- MOBILE OVERLAY -->
    <div
        v-if="isMobile && sidebarOpen"
        class="overlay"
        @click="sidebarOpen=false"
    />

    <!-- SIDEBAR -->
    <aside
        class="sidebar"
        :class="{
        open: sidebarOpen,
        mobile: isMobile,
        collapsed: !sidebarOpen && !isMobile
      }"
    >
      <div class="sidebar-header">
        <div class="sidebar-title">
          <h3>
            {{ t('chats') }}
            <span
                v-if="mqttStatus !== 'idle'"
                class="live-dot"
                :class="`live-${mqttStatus}`"
                :title="liveStatusLabel"
            ></span>
          </h3>
          <div v-if="domainLocal" class="sidebar-domain">{{ domainLocal }}</div>
        </div>

        <div class="header-actions">
          <button v-if="!props.session || props.unverified" class="login-btn" @click="emit('show-login')">{{ props.unverified ? t('openExistingChat') : t('login') }}</button>
          <button v-if="isMobile" class="icon-btn" @click="sidebarOpen=false">✕</button>
        </div>
      </div>

      <div class="config">
        <input
            v-model="filterText"
            :placeholder="t('filter')"
            class="filter-input"
        />
        <button
            v-if="canCreateClient"
            class="reload-btn"
            @click="onShowNewClient"
            :title="t('newChat')"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <line x1="19" y1="8" x2="19" y2="14"/>
            <line x1="22" y1="11" x2="16" y2="11"/>
          </svg>
        </button>
        <button class="reload-btn" @click="reload" :title="t('reconnect')" :disabled="loadingConnections">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 2v6h-6"/>
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
            <path d="M3 22v-6h6"/>
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
          </svg>
        </button>
      </div>

      <div class="connection-list">
        <div v-if="loadingConnections" class="loading-indicator">{{ t('loading') }}</div>
        <div
            v-for="c in filteredConnections"
            :key="c.id"
            class="connection"
            :class="{ active: selected?.id === c.id }"
            @click="selectConnection(c)"
        >
          <img
              class="conn-img"
              :src="connImageUrl(c)"
              @error="$event.target.style.display='none'; $event.target.nextElementSibling.style.display='flex'"
          />
          <div class="conn-avatar" style="display:none">{{ c.name?.charAt(0)?.toUpperCase() || '?' }}</div>
          <div class="conn-info">
            <div class="conn-name">{{ c.name }}</div>
            <div v-if="showConnDetails && c.email && c.email !== c.name" class="conn-email">{{ c.email }}</div>
            <div v-if="showConnDetails && c.number" class="conn-number">{{ c.number }}</div>
          </div>
          <span v-if="unreadCount(c) > 0" class="badge">{{ unreadCount(c) }}</span>
        </div>
      </div>
    </aside>

    <!-- CHAT -->
    <main class="chat">

      <header class="chat-header">
        <button class="icon-btn" :title="t('chats')" @click="toggleSidebar">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="5" width="6" height="14" rx="1"/>
            <rect x="11" y="5" width="10" height="14" rx="1"/>
          </svg>
        </button>

        <img
            v-if="selected"
            class="header-conn-img"
            :class="{ clickable: canEditPartner }"
            :src="connImageUrl(selected)"
            :title="canEditPartner ? t('partnerProfile') : ''"
            @click="onPartnerImageClick"
            @error="$event.target.style.display='none'"
        />
        <h2 v-if="selected">{{ selected.name }}</h2>
        <h2 v-else class="placeholder-title">{{ t('hashChat') }}</h2>

        <!-- „Alle gelesen": nur sichtbar, wenn die offene Verbindung ungelesene Nachrichten hat -->
        <button
            v-if="hasUnreadInSelected"
            class="mark-all-read"
            :title="t('markAllRead')"
            @click="markAllRead"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 7l-8 8-4-4"/>
            <path d="M22 9l-8 8-2-2"/>
          </svg>
          <span class="mark-all-read-label">{{ t('markAllRead') }}</span>
        </button>

        <div v-if="props.session && !props.unverified" class="settings-menu">
          <button
              class="icon-btn"
              :title="t('settings')"
              @click.stop="settingsOpen = !settingsOpen"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <div v-if="settingsOpen" class="settings-dropdown" @click.stop>
            <button @click="onShowProfile">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              <span>{{ t('profile') }}</span>
            </button>
            <a :href="folderUrl" class="dropdown-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              </svg>
              <span>{{ t('files') }}</span>
            </a>
            <button @click="onChangePassword">
              <span>{{ t('changePassword') }}</span>
            </button>
            <button v-if="isDomainUser" @click="onShowIntegration">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
              <span>{{ t('chatIntegration') }}</span>
            </button>
            <button @click="onShowTerms">
              <span>{{ t('terms') }}</span>
            </button>
            <button @click="onLogoutClick">
              <span>{{ t('logout') }}</span>
            </button>
          </div>
        </div>

        <button
            v-if="props.embedMode"
            class="icon-btn embed-close"
            :title="t('close')"
            @click="onEmbedClose"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </header>

      <div class="messages" ref="container">
        <div v-if="loadingMessages" class="loading-indicator">{{ t('loading') }}</div>
        <div v-if="guestMode" class="guest-welcome">{{ t('welcomeMessage') }}</div>
        <div v-if="guestMode && guestError" class="guest-error" role="alert">{{ t(guestError) }}</div>
        <template v-for="(m, i) in messages" :key="m.id">
          <div
              :class="['msg-row', m.isOwn ? 'me' : 'them', { unread: isMessageUnread(m) }]"
              @click="onMessageClick(m, $event)"
          >
            <img v-if="m.image" :src="m.image" class="avatar" />
            <div v-else class="avatar-placeholder">{{ m.name?.charAt(0)?.toUpperCase() || '?' }}</div>

            <div class="bubble">
              <div class="sender">{{ m.name }}</div>
              <div class="text markdown" v-html="renderMarkdown(m.html || m.text)"></div>
              <div class="time">{{ formatDate(m.date) }}</div>

              <!-- Click an own message to reveal edit (CHAT origin only) / delete -->
              <div v-if="m.isOwn && msgMenuId === m.id" class="msg-menu" @click.stop>
                <button v-if="m.origin === 'CHAT'" @click="startEdit(m)">{{ t('edit') }}</button>
                <button class="danger" @click="askDelete(m)">{{ t('delete') }}</button>
              </div>
            </div>
          </div>
          <div v-if="i === unreadDividerIndex" class="unread-divider">
            <span>{{ t('unread') }}</span>
          </div>
        </template>
      </div>

      <!-- Inline, non-blocking save prompt while the visitor is anonymous/unverified -->
      <SaveChatPrompt
          v-if="props.unverified && !savePromptDismissed && !verifiedNotice"
          :busy="saveBusy"
          :initialEmail="saveEmail"
          @send-code="onSaveSendCode"
          @dismiss="savePromptDismissed = true"
      />

      <!-- Success banner after the email address was verified -->
      <div v-if="verifiedNotice" class="verified-notice">
        <h4>{{ t('verifiedTitle') }}</h4>
        <p>{{ t('verifiedBody') }}</p>
        <button class="primary-btn" @click="verifiedNotice = false">{{ t('continueChat') }}</button>
      </div>

      <footer v-if="selected || guestMode" class="composer">
        <button v-if="selected && !props.unverified" class="attach-btn" @click="openAttachment" :disabled="!service" :title="t('attachFile')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
          </svg>
        </button>
        <textarea
            ref="draftInput"
            v-model="draft"
            @focus="$event.target.rows = 4"
            @blur="$event.target.rows = 1"
            @keydown.enter.ctrl.prevent="send"
            @keydown.enter.meta.prevent="send"
            :placeholder="t('writeMessage')"
            rows="1"
        />
        <button class="send-btn" @click="send" :disabled="!canSend">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </footer>

      <div v-if="!selected && !guestMode" class="empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#b0b8c9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <p>{{ t('selectChat') }}</p>
      </div>

    </main>

    <ChatProfile
        v-if="profileOpen"
        :session="props.session"
        :imageBaseUrl="profileImageBaseUrl"
        :service="service"
        :connectionId="profileConnectionId"
        @close="profileOpen = false"
        @image-updated="onProfileImageUpdated"
    />

    <ChatConnectionUser
        v-if="partnerProfileOpen && selected && service"
        :service="service"
        :connectionId="selected.id"
        @close="partnerProfileOpen = false"
        @saved="onPartnerProfileSaved"
    />

    <ChatTerms
        v-if="termsOpen"
        @close="termsOpen = false"
    />

    <ChatNewClient
        v-if="newClientOpen"
        :service="service"
        @close="newClientOpen = false"
        @created="onNewClientCreated"
    />

    <ChatIntegration
        v-if="integrationOpen && isDomainUser"
        :tenantId="props.session?.domain || ''"
        @close="integrationOpen = false"
    />

    <ChatAttachmentDialog
        v-if="attachmentOpen && selected && service"
        :service="service"
        :connectionId="selected.id"
        :initialText="draft"
        @sent="onAttachmentSent"
        @close="onAttachmentClose"
    />

    <EmailCodeDialog
        v-if="emailCodeOpen && service"
        :service="service"
        :email="saveEmail"
        @success="onEmailCodeSuccess"
        @cancel="emailCodeOpen = false"
    />

    <MessageEditDialog
        v-if="editTarget && service && selected"
        :service="service"
        :connectionId="selected.id"
        :message="editTarget"
        @saved="onMessageEdited"
        @close="editTarget = null"
    />

    <ConfirmDialog
        v-if="deleteTarget"
        :title="t('deleteMessage')"
        :message="t('deleteConfirmBody')"
        :confirmLabel="t('delete')"
        :busy="deleteBusy"
        @confirm="confirmDelete"
        @cancel="deleteTarget = null"
    />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import ChatApiService from '../services/ChatApiService'
import ChatMqttClient from '../services/ChatMqttClient.js'
import ChatProfile from './ChatProfile.vue'
import ChatConnectionUser from './ChatConnectionUser.vue'
import ChatTerms from './ChatTerms.vue'
import ChatNewClient from './ChatNewClient.vue'
import ChatIntegration from './ChatIntegration.vue'
import ChatAttachmentDialog from './ChatAttachmentDialog.vue'
import SaveChatPrompt from './SaveChatPrompt.vue'
import EmailCodeDialog from './EmailCodeDialog.vue'
import MessageEditDialog from './MessageEditDialog.vue'
import ConfirmDialog from './ConfirmDialog.vue'
import { useI18n } from '../i18n.js'

marked.setOptions({
  breaks: true,
  gfm: true
})

const props = defineProps({
  session: Object,
  connectionId: { type: [String, Number], default: null },
  pendingMessage: { type: String, default: '' },
  pendingHashhashDomain: { type: String, default: '' },
  pendingClientAccountName: { type: String, default: '' },
  embedMode: { type: Boolean, default: false },
  // account##domain of the embedding company; enables the anonymous guest flow.
  domainAccount: { type: String, default: '' },
  // true while the visitor is in the anonymous/temp state (after /temp/start, before
  // email verification). Drives the reduced "no extra menus" UI + SaveChatPrompt.
  unverified: { type: Boolean, default: false }
})

const emit = defineEmits(['logout', 'show-login', 'show-change-password', 'pending-sent', 'temp-started', 'verified'])

const { t } = useI18n()

/* Anonymous guest mode: embedded, a company is known, nobody is logged in yet, and we are
   not already in the temp/unverified state. The visitor can type immediately; the first
   send starts a temp chat. */
const guestMode = computed(() =>
    props.embedMode && !!props.domainAccount && !props.session && !props.unverified
)

const domainLocal = ref('default')
const service = ref(null)
let mqttClient = null
const subscribedDomains = new Set()
let blinkTopics = []          // Blinklicht-Topics dieses Aufrufers (leer = alter Weg)
let blinkRefreshHandle = null

const POLL_INTERVAL_MS = 20000
const mqttStatus = ref('idle')
let pollIntervalHandle = null

const liveStatusLabel = computed(() => {
  if (mqttStatus.value === 'connected') return t('liveUpdates')
  if (mqttStatus.value === 'failed') return t('periodicRefresh')
  return t('connecting')
})

watch(mqttStatus, (s) => {
  if (s === 'connected') {
    stopPolling()
  } else if (s === 'failed') {
    startPolling()
  }
})

const connections = ref([])
const selected = ref(null)
const messages = ref([])
const drafts = ref({})
const container = ref(null)
const draftInput = ref(null)

/* Anonymous onboarding state */
const guestDraft = ref('')              // composer text before a connection exists
const guestError = ref('')              // i18n key when /temp/start was refused (rate limit, not available)
const tempChat = ref(null)              // { connectionId, anonId } after /temp/start
const saveEmail = ref('')               // email entered in the SaveChatPrompt
const saveBusy = ref(false)             // requesting the email code
const savePromptDismissed = ref(false)  // user hit "Später" — re-shown on next message
const emailCodeOpen = ref(false)        // EmailCodeDialog overlay
const verifiedNotice = ref(false)       // success banner after verification

/* Message edit/delete */
const msgMenuId = ref(null)             // id of the message whose action menu is open
const editTarget = ref(null)            // message being edited (MessageEditDialog)
const deleteTarget = ref(null)          // message pending delete confirmation
const deleteBusy = ref(false)

/* Provide a service in guest mode (no session yet) so /temp/start can be called.
   The session-driven init() builds the real service once a session exists. */
watch(guestMode, (g) => {
  if (g && !service.value) service.value = new ChatApiService('default', {})
}, { immediate: true })

/* When new messages arrive while still unverified, re-show a dismissed save prompt. */
watch(() => messages.value.length, () => {
  if (props.unverified) savePromptDismissed.value = false
})

/*
 * Unread semantics (driven entirely by message.status):
 *   ANY message with status === 'NEW' is visually rendered as unread,
 *   including own messages (they're "unread" because the partner hasn't
 *   acknowledged them yet).
 *   The divider, however, only marks where the current user still has
 *   unread PARTNER messages (!isOwn) — own unread messages never cause a
 *   divider.
 *   Only partner messages can be acknowledged via click.
 *
 * Connection badge count:
 *   Domain user sees `clientUnreadCount` (how many messages the client
 *   has not yet read). Client user sees `domainUnreadCount` (mirror).
 */

const unreadDividerIndex = computed(() => {
  if (!selected.value || messages.value.length < 2) return -1
  const firstUnread = messages.value.findIndex(m => !m.isOwn && m.status === 'NEW')
  if (firstUnread <= 0) return -1
  return firstUnread - 1
})

function isMessageUnread(m) {
  return m.status === 'NEW'
}

function unreadCount(c) {
  // Show the viewer's own unread inbox count:
  //   Client sees clientUnreadCount (domain messages waiting to be read by the client).
  //   Admin/domain user sees domainUnreadCount (client messages waiting to be read by the admin).
  return props.session?.clientLogin ? (c.clientUnreadCount || 0) : (c.domainUnreadCount || 0)
}

/* Total unread across all connections — used to drive the host-page badge in
   embed mode. Naturally tenant-scoped because connections themselves are. */
const totalUnread = computed(() =>
    connections.value.reduce((sum, c) => sum + unreadCount(c), 0)
)

/* Hat die aktuell geöffnete Verbindung ungelesene Nachrichten des Gegenübers?
   Steuert den „Alle gelesen"-Knopf im Kopf der Konversation. */
const hasUnreadInSelected = computed(() => !!selected.value && unreadCount(selected.value) > 0)

/* Origin of the embedding page, so postMessage never goes to '*'. Chrome/Safari expose
   location.ancestorOrigins; elsewhere the referrer carries at least the origin (default
   Referrer-Policy). Unknown -> nothing is sent. Whether that origin may frame us at all is
   decided server-side (frame-ancestors per tenant, ChatEmbedPolicy). */
function parentOrigin() {
  try {
    const ao = window.location.ancestorOrigins
    if (ao && ao.length) return ao[0]
  } catch (e) { /* ignore */ }
  try {
    if (document.referrer) return new URL(document.referrer).origin
  } catch (e) { /* ignore */ }
  return null
}

function postToParent(msg) {
  if (!props.embedMode) return
  if (window.parent === window) return
  const origin = parentOrigin()
  if (!origin || origin === 'null') return
  try { window.parent.postMessage(msg, origin) } catch (e) { /* ignore */ }
}

watch(totalUnread, (n) => postToParent({ type: 'tibtix-unread', count: n }))

function onEmbedClose() {
  postToParent({ type: 'tibtix-close-request' })
}

async function acknowledgeMessage(m) {
  // Only partner messages can be acknowledged; own NEW messages show status
  // but the current user cannot flip them to READ (that's the partner's action).
  if (!selected.value || m.isOwn || m.status !== 'NEW' || !m.date) return
  const timestamp = m.date.getTime()
  const selectedId = selected.value.id
  await service.value.resetChatLastViewed(selectedId, timestamp)
  // Optimistic local update: all partner NEW messages up to and including this one become READ
  for (const msg of messages.value) {
    if (!msg.isOwn && msg.status === 'NEW' && msg.date && msg.date.getTime() <= timestamp) {
      msg.status = 'READ'
    }
  }
  // Refresh connections so the sidebar unread badges reflect the new backend state
  await loadConnections()
  const refreshed = connections.value.find(c => c.id === selectedId)
  if (refreshed) selected.value = refreshed
}

/* „Alle gelesen": alle ungelesenen Nachrichten des Gegenübers in dieser Verbindung
   als gelesen markieren (lastViewed auf den neuesten Nachrichten-Zeitstempel setzen). */
async function markAllRead() {
  if (!selected.value) return
  const selectedId = selected.value.id
  let timestamp = 0
  for (const m of messages.value) {
    if (m.date && m.date.getTime() > timestamp) timestamp = m.date.getTime()
  }
  if (!timestamp) return
  await service.value.resetChatLastViewed(selectedId, timestamp)
  // Optimistisch: alle ungelesenen Partner-Nachrichten bis zu diesem Zeitpunkt werden READ
  for (const msg of messages.value) {
    if (!msg.isOwn && msg.status === 'NEW' && msg.date && msg.date.getTime() <= timestamp) {
      msg.status = 'READ'
    }
  }
  await loadConnections()
  const refreshed = connections.value.find(c => c.id === selectedId)
  if (refreshed) selected.value = refreshed
}

const draft = computed({
  get: () => {
    if (guestMode.value) return guestDraft.value
    return selected.value ? (drafts.value[selected.value.id] || '') : ''
  },
  set: (val) => {
    if (guestMode.value) {
      guestDraft.value = val
    } else if (selected.value) {
      drafts.value[selected.value.id] = val
    }
  }
})
const tibVersion = ref(0)

const loadingConnections = ref(false)
const loadingMessages = ref(false)

const sidebarOpen = ref(true)
const isMobile = ref(false)
const reloadVersion = ref(0)
const settingsOpen = ref(false)
const profileOpen = ref(false)
const partnerProfileOpen = ref(false)
const termsOpen = ref(false)
const newClientOpen = ref(false)
const integrationOpen = ref(false)
const attachmentOpen = ref(false)
const filterText = ref('')

const isDomainUser = computed(() => !!props.session && !props.session.clientLogin)

const canCreateClient = computed(() => !!props.session && !props.session.clientLogin)
const showConnDetails = computed(() => !!props.session && !props.session.clientLogin)

/* Domain user can click the partner image in the header to open the partner's
   profile (= the client behind this connection). Clients don't get this affordance. */
const canEditPartner = computed(() => !!props.session && !props.session.clientLogin && !!selected.value)

/* Connection ID passed to <ChatProfile> so clients can edit their own profile.
   For domain users we don't pass one — they edit partner profiles via the dedicated
   <ChatConnectionUser> dialog instead, so the form stays hidden in their own profile view. */
const profileConnectionId = computed(() => {
  if (!props.session?.clientLogin) return null
  return selected.value?.id || connections.value[0]?.id || null
})

/* Filter on a flat string of all reasonably searchable connection fields:
   display name, email / client identifier, partner account, number, chat notes
   and folder path. Case-insensitive substring match. */
const filteredConnections = computed(() => {
  const q = filterText.value.trim().toLowerCase()
  if (!q) return connections.value
  return connections.value.filter(c => {
    const fields = [c.name, c.email, c.clientIdentifier, c.domainAccountName, c.clientAccountName, c.number, c.flags, c.chatNotes, c.path]
    const haystack = fields
        .filter(v => v != null && v !== '')
        .map(v => String(v).toLowerCase())
        .join(' ')
    return haystack.includes(q)
  })
})
/* Tracks the last successfully sent draft per connection to prevent duplicate sends. */
const lastSent = ref({})

/* True while a send request is in flight, to block concurrent duplicate sends. */
const sending = ref(false)

const canSend = computed(() => {
  if (sending.value) return false
  const trimmed = draft.value.trim()
  if (!trimmed) return false
  if (guestMode.value) return true
  if (!selected.value) return false
  const last = lastSent.value[selected.value.id]
  return last == null || trimmed !== last
})

/* URL of the currently logged-in user's own profile image.
   For client users, every connection's `userId` is their own -> /api/image/{userId}/user.
   For domain users we fall back to the session-based endpoint. */
const profileImageBaseUrl = computed(() => {
  if (props.session?.clientLogin && connections.value.length) {
    const uid = connections.value[0].userId
    if (uid) return `/tt/api/image/${encodeURIComponent(uid)}/user`
  }
  return '/tt/api/image/'
})

/* init from session */
watch(() => props.session, (s) => {
  if (s?.domain) {
    domainLocal.value = s.domain
    init()
  }
}, { immediate: true })

/* mobile: show sidebar when no chat selected — except for a guest, who has no chats yet and
   would otherwise get the (empty) list over the composer */
watch(selected, (val) => {
  if (isMobile.value && !val && !guestMode.value) {
    sidebarOpen.value = true
  }
})

function updateMobile() {
  isMobile.value = window.innerWidth < 768
  sidebarOpen.value = !isMobile.value || (!selected.value && !guestMode.value)
}

onMounted(() => {
  updateMobile()
  window.addEventListener('resize', updateMobile)
  window.addEventListener('click', closeSettingsOnOutsideClick)
  ChatApiService.setTibNameListener(() => { tibVersion.value++ })
  postToParent({ type: 'tibtix-ready' })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMobile)
  window.removeEventListener('click', closeSettingsOnOutsideClick)
  stopPolling()
  if (mqttClient) {
    mqttClient.disconnect()
    mqttClient = null
    subscribedDomains.clear()
  }
  if (blinkRefreshHandle) { clearTimeout(blinkRefreshHandle); blinkRefreshHandle = null }
  blinkTopics = []
  mqttStatus.value = 'idle'
})

async function setupMqtt() {
  if (mqttClient || !service.value) return
  const config = await service.value.getMqttConfig()
  if (!config || !config.configured) {
    // No broker configured — fall back to polling so updates still arrive.
    mqttStatus.value = 'failed'
    return
  }
  // Blinklicht (Protect7 RT1-(1)): der Server liefert pro Aufrufer bedeutungslose, taeglich wechselnde Topics.
  // Eine (leere) Nachricht darauf heisst nur "lade neu" — der Inhalt kommt ueber die normale, angemeldete API.
  blinkTopics = Array.isArray(config.blinkTopics) ? config.blinkTopics : []
  mqttClient = new ChatMqttClient(config)
  mqttClient.onStatus((s) => {
    mqttStatus.value = s
    if (s === 'connected' && blinkTopics.length) {
      mqttClient.subscribeExact(blinkTopics)
      scheduleBlinkRefresh(config.blinkValidUntil)
    }
  })
  mqttClient.onMessage((topic, payload) => {
    handleMqttChatChange(topic, payload)
  })
  mqttClient.connect()
}

// Topics gelten bis Mitternacht UTC; danach neue holen und abonnieren.
function scheduleBlinkRefresh(validUntil) {
  if (blinkRefreshHandle) clearTimeout(blinkRefreshHandle)
  const delay = Math.max(60000, (Number(validUntil) || 0) - Date.now() + 2000)
  blinkRefreshHandle = setTimeout(async () => {
    try {
      const cfg = await service.value.getMqttConfig()
      blinkTopics = Array.isArray(cfg && cfg.blinkTopics) ? cfg.blinkTopics : []
      if (mqttClient && blinkTopics.length) mqttClient.subscribeExact(blinkTopics)
      scheduleBlinkRefresh(cfg && cfg.blinkValidUntil)
    } catch (e) { /* beim naechsten Mal erneut */ }
  }, delay)
}

function startPolling() {
  if (pollIntervalHandle) return
  pollIntervalHandle = setInterval(async () => {
    if (!service.value) return
    try {
      await loadConnections()
      subscribeForConnections()
      if (selected.value) await loadMessages()
    } catch (e) { /* ignore — next tick will try again */ }
  }, POLL_INTERVAL_MS)
}

function stopPolling() {
  if (pollIntervalHandle) {
    clearInterval(pollIntervalHandle)
    pollIntervalHandle = null
  }
}

function subscribeForConnections() {
  if (!mqttClient) return
  if (blinkTopics.length) return   // Blinklicht aktiv: feste Topics statt Domain-Wildcard
  for (const c of connections.value) {
    if (!c.hashhashDomain) continue
    if (subscribedDomains.has(c.hashhashDomain)) continue
    mqttClient.subscribeForDomain(c.hashhashDomain)
    subscribedDomains.add(c.hashhashDomain)
  }
}

async function handleMqttChatChange(topic, payload) {
  // Blinklicht: bedeutungsloses Topic (/n/...) mit leerer Nachricht -> "lade neu" (kein Inhalt im Kanal).
  if (typeof topic === 'string' && topic.indexOf('/n/') >= 0) {
    await loadConnections()
    if (selected.value) await loadMessages()
    return
  }

  let data = null
  try {
    data = JSON.parse(payload.toString())
  } catch (e) { /* malformed — fall through to reload */ }

  const connectionKey = data?.connectionKey || ChatMqttClient.parseConnectionKey(topic)
  if (!connectionKey) return

  const conn = connections.value.find(c => c.id === connectionKey)

  if (conn && data) {
    // In-place update so sidebar badge + divider reflect the new state without a full reload
    if (data.version != null) conn.version = data.version
    if (data.clientLastViewed != null) conn.clientLastViewed = data.clientLastViewed
    if (data.domainLastViewed != null) conn.domainLastViewed = data.domainLastViewed
    if (data.clientUnreadCount != null) conn.clientUnreadCount = data.clientUnreadCount
    if (data.domainUnreadCount != null) conn.domainUnreadCount = data.domainUnreadCount
  } else {
    // Unknown connection or no parseable payload — refresh the full list
    await loadConnections()
    subscribeForConnections()
  }

  if (selected.value?.id === connectionKey) {
    await loadMessages()
  }
}

const folderUrl = computed(() => {
  if (selected.value) {
    return `/tt/view/connection/${selected.value.id}/folder`
  }
  return '/tt/'
})

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
}

/* LOGOUT */
async function logout() {
  try {
    await fetch('/tt/api/logout', { method: 'GET', credentials: 'include' })
  } catch (e) { /* ignore */ }
  stopPolling()
  if (mqttClient) {
    mqttClient.disconnect()
    mqttClient = null
    subscribedDomains.clear()
  }
  if (blinkRefreshHandle) { clearTimeout(blinkRefreshHandle); blinkRefreshHandle = null }
  blinkTopics = []
  mqttStatus.value = 'idle'
  service.value = null
  connections.value = []
  selected.value = null
  messages.value = []
  emit('logout')
}

function onLogoutClick() {
  settingsOpen.value = false
  logout()
}

function onChangePassword() {
  settingsOpen.value = false
  emit('show-change-password')
}

function onShowProfile() {
  settingsOpen.value = false
  profileOpen.value = true
}

function onShowTerms() {
  settingsOpen.value = false
  termsOpen.value = true
}

function onShowIntegration() {
  settingsOpen.value = false
  integrationOpen.value = true
}

function onShowNewClient() {
  if (!canCreateClient.value || !service.value) return
  newClientOpen.value = true
}

function onPartnerImageClick() {
  if (!canEditPartner.value) return
  partnerProfileOpen.value = true
}

async function onPartnerProfileSaved(updated) {
  // Display name on the connection may have changed — refresh sidebar so the new name shows up.
  await loadConnections()
  const refreshed = connections.value.find(c => c.id === selected.value?.id)
  if (refreshed) selected.value = refreshed
}

async function onNewClientCreated() {
  newClientOpen.value = false
  await loadConnections()
  subscribeForConnections()
}

function onProfileImageUpdated() {
  // Bust image caches so the updated profile photo appears everywhere
  reloadVersion.value++
}

function closeSettingsOnOutsideClick() {
  if (settingsOpen.value) settingsOpen.value = false
  if (msgMenuId.value !== null) msgMenuId.value = null
}

/* INIT */
async function init() {
  if (!domainLocal.value) return
  service.value = new ChatApiService(domainLocal.value, props.session)
  await loadConnections()
  await setupMqtt()
  subscribeForConnections()

  if (props.pendingHashhashDomain && props.pendingClientAccountName) {
    const match = connections.value.find(c =>
        c.hashhashDomain === props.pendingHashhashDomain
        && c.clientAccountName === props.pendingClientAccountName
    )
    if (match) {
      if (props.pendingMessage) {
        drafts.value[match.id] = props.pendingMessage
      }
      await selectConnection(match)
      return
    }
  }

  if (props.connectionId) {
    const match = connections.value.find(c => String(c.id) === String(props.connectionId))
    if (match) {
      // Vorausgefüllter Composer-Text (z.B. tib://-Link aus der TiB-Ansicht),
      // aber nur wenn der Benutzer noch nichts Eigenes im Entwurf hat.
      if (props.pendingMessage && !(drafts.value[match.id] || '').trim()) {
        drafts.value[match.id] = props.pendingMessage
      }
      await selectConnection(match)
    }
  } else if (connections.value.length === 1) {
    await selectConnection(connections.value[0])
  }
}

/* CONNECTIONS */
async function loadConnections() {
  loadingConnections.value = true
  try {
    connections.value = await service.value.getConnections()
  } finally {
    loadingConnections.value = false
  }
}

async function selectConnection(c) {
  selected.value = c
  updateUrlConnectionId(c?.id)
  await loadMessages()

  if (isMobile.value) {
    sidebarOpen.value = false
  }

  await nextTick()
  if (draft.value && draftInput.value) {
    draftInput.value.focus()
    const len = draft.value.length
    draftInput.value.setSelectionRange(len, len)
  }
}

function updateUrlConnectionId(id) {
  const url = new URL(window.location.href)
  if (id == null || id === '') {
    url.searchParams.delete('connectionId')
  } else {
    url.searchParams.set('connectionId', String(id))
  }
  window.history.replaceState({}, '', url.toString())
}

/* MESSAGES */
/* Monotonic token guarding against out-of-order getMessages responses.
   Several loadMessages() can be in flight at once (chat selection, polling, and every
   MQTT event). Without this guard the LAST response to resolve wins — even if it read
   staler server data — which can clobber a just-sent message until a manual reload.
   Bumped on every load AND on every optimistic append, so a stale fetch can never
   overwrite newer local state. */
let messagesRequestId = 0

/* Append messages without duplicating ones already present (by id), and invalidate any
   in-flight loadMessages so a stale server list can't drop what we just added. */
function appendMessages(newMsgs) {
  const known = new Set(messages.value.map(m => m.id))
  const toAdd = newMsgs.filter(m => !known.has(m.id))
  if (toAdd.length) messages.value = [...messages.value, ...toAdd]
  messagesRequestId++
}

async function loadMessages() {
  const requestId = ++messagesRequestId
  const connectionId = selected.value.id
  loadingMessages.value = true
  try {
    const fetched = await service.value.getMessages(connectionId)
    // Discard stale/out-of-order results: a newer load (or append) has superseded this
    // one, or the user switched to a different chat while it was in flight.
    if (requestId !== messagesRequestId || selected.value?.id !== connectionId) return
    messages.value = fetched
    ChatApiService.resolveTibNames(messages.value)
    const c = selected.value
    const fmt = (v) => (v == null ? '—' : new Date(v).toLocaleString())
    console.log(`[Chat ${c.name}] clientLastViewed=${fmt(c.clientLastViewed)}  domainLastViewed=${fmt(c.domainLastViewed)}`)
    await nextTick()
    scrollBottom()
  } finally {
    if (requestId === messagesRequestId) loadingMessages.value = false
  }
}

async function send() {
  if (!canSend.value) return

  // Anonymous first message: start a temp chat instead of posting to a connection.
  if (guestMode.value) {
    await startGuestChat()
    return
  }

  const wasPending = !!props.pendingMessage
  const connectionId = selected.value.id
  const sentText = draft.value.trim()

  sending.value = true
  try {
    const newMsgs = await service.value.sendMessage(
        connectionId,
        draft.value
    )

    ChatApiService.resolveTibNames(newMsgs)
    appendMessages(newMsgs)
    lastSent.value[connectionId] = sentText
    draft.value = ''

    if (wasPending) {
      emit('pending-sent')
    }

    await nextTick()
    scrollBottom()
  } finally {
    sending.value = false
  }
}

/* First message of an anonymous visitor: create a temp chat + account server-side and
   post the first message. App reacts to `temp-started` by refreshing the session (the
   visitor is now an anonymous client) and passing the connectionId back, which re-runs
   init() and auto-selects the new connection. */
async function startGuestChat() {
  const text = guestDraft.value.trim()
  sending.value = true
  guestError.value = ''
  try {
    const res = await service.value.startTempChat(props.domainAccount, text)
    tempChat.value = { connectionId: res.connectionId, anonId: res.anonId }
    guestDraft.value = ''
    emit('temp-started', { connectionId: res.connectionId, anonId: res.anonId })
  } catch (e) {
    console.error('[Chat] temp start failed', e)
    guestError.value = e?.status === 429 ? 'guestRateLimited' : 'guestUnavailable'
  } finally {
    sending.value = false
  }
}

/* SaveChatPrompt: request a 6-digit code, then open the verification dialog. */
async function onSaveSendCode(email) {
  saveEmail.value = email
  saveBusy.value = true
  try {
    await service.value.requestEmailCode(email)
  } catch (e) { /* neutral */ } finally {
    saveBusy.value = false
  }
  emailCodeOpen.value = true
}

/* EmailCodeDialog success: the session is now the email account. Claim the anonymous chat
   (connectionId stays the same), then let App refresh the session via `verified`. */
async function onEmailCodeSuccess() {
  emailCodeOpen.value = false
  try {
    if (tempChat.value?.anonId) await service.value.claimTempChat(tempChat.value.anonId)
  } catch (e) { /* best-effort — the chat already exists under the new session */ }
  tempChat.value = null
  emit('verified')
  verifiedNotice.value = true
}

/* Apply an updated message page returned by edit/delete. Replaces the list (like
   loadMessages) and invalidates any in-flight load so a stale fetch can't undo it. */
function applyMessagePage(msgs) {
  ChatApiService.resolveTibNames(msgs)
  messages.value = msgs
  messagesRequestId++
}

/* Clicking a message: own messages toggle the edit/delete menu; partner messages
   acknowledge (mark read). stopPropagation keeps the window outside-click handler from
   immediately closing the menu we just opened. */
function onMessageClick(m, e) {
  if (m.isOwn) {
    e.stopPropagation()
    msgMenuId.value = msgMenuId.value === m.id ? null : m.id
  } else {
    acknowledgeMessage(m)
  }
}

function startEdit(m) {
  msgMenuId.value = null
  editTarget.value = m
}

function askDelete(m) {
  msgMenuId.value = null
  deleteTarget.value = m
}

async function onMessageEdited(msgs) {
  editTarget.value = null
  applyMessagePage(msgs)
  await nextTick()
  scrollBottom()
}

async function confirmDelete() {
  if (!deleteTarget.value || !selected.value || !service.value) return
  deleteBusy.value = true
  try {
    const msgs = await service.value.deleteMessage(selected.value.id, deleteTarget.value.id)
    applyMessagePage(msgs)
    deleteTarget.value = null
    await nextTick()
    scrollBottom()
  } catch (e) {
    console.error('[Chat] delete failed', e)
  } finally {
    deleteBusy.value = false
  }
}

function openAttachment() {
  if (!selected.value || !service.value) return
  attachmentOpen.value = true
}

/* Dialog cancelled/closed: keep the (possibly edited) caption in the draft so nothing is lost. */
function onAttachmentClose(text) {
  if (selected.value) draft.value = text || ''
  attachmentOpen.value = false
}

/* File sent successfully: mirror send()'s success handling. */
async function onAttachmentSent(newMsgs) {
  attachmentOpen.value = false
  ChatApiService.resolveTibNames(newMsgs)
  appendMessages(newMsgs)
  draft.value = ''
  await nextTick()
  scrollBottom()
}

function scrollBottom() {
  if (!container.value) return
  container.value.scrollTop = container.value.scrollHeight
}

function renderMarkdown(text) {
  if (!text) return ''
  void tibVersion.value
  // Sicherheit: marked laesst Inline-HTML durch — vor dem v-html IMMER durch
  // DOMPurify. Entfernt <script>, Event-Handler (onerror/onload), javascript:-
  // URLs u. Ae. und schuetzt damit vor Stored-XSS (Protect7). Markdown-
  // Formatierung (fett, Links, Listen) bleibt erhalten.
  const rawHtml = marked.parse(ChatApiService.replaceTibLinks(text))
  return DOMPurify.sanitize(rawHtml)
}

function connImageUrl(c) {
  if (!c?.imageUrl) return ''
  if (reloadVersion.value === 0) return c.imageUrl
  const sep = c.imageUrl.includes('?') ? '&' : '?'
  return `${c.imageUrl}${sep}imageVersion=${reloadVersion.value}`
}

function reload() {
  reloadVersion.value++
  return init()
}

function formatDate(d) {
  const date = new Date(d)
  const now = new Date()
  const pad = n => String(n).padStart(2, '0')
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`

  if (date.getDate() === now.getDate()
      && date.getMonth() === now.getMonth()
      && date.getFullYear() === now.getFullYear()) {
    return time
  }

  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} - ${time}`
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app {
  display: flex;
  height: 100vh;
  height: 100dvh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow: hidden;
  background: #f0f2f5;
  color: #1a1a2e;
}

/* OVERLAY MOBILE */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 5;
  backdrop-filter: blur(2px);
}

/* SIDEBAR */
.sidebar {
  width: 300px;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e4e8ee;
  transition: all .25s ease;
  z-index: 10;
}

.sidebar.collapsed {
  width: 0;
  min-width: 0;
  overflow: hidden;
  border-right: none;
}

.sidebar.mobile {
  position: fixed;
  left: -300px;
  top: 0;
  bottom: 0;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
}

.sidebar.mobile.open {
  left: 0;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 12px;
  border-bottom: 1px solid #e4e8ee;
}

.sidebar-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.sidebar-title {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.sidebar-domain {
  font-size: 11px;
  color: #6b7280;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.live-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 6px;
  vertical-align: middle;
  background: #9ca3af;
}

.live-dot.live-connected {
  background: #16a34a;
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.18);
}

.live-dot.live-failed {
  background: #f59e0b;
}

.live-dot.live-connecting {
  background: #9ca3af;
  animation: live-pulse 1.2s ease-in-out infinite;
}

@keyframes live-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

.header-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

/* SHARED BUTTONS */
.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: 8px;
  cursor: pointer;
  color: #5f6b7a;
  transition: background .15s, color .15s;
}

.icon-btn:hover {
  background: #f0f2f5;
  color: #1a1a2e;
}

.logout-btn {
  border: none;
  background: #f0f2f5;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #5f6b7a;
  transition: background .15s, color .15s;
}

.logout-btn:hover {
  background: #e4e8ee;
  color: #1a1a2e;
}

.login-btn {
  border: none;
  background: #4f46e5;
  color: white;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: background .15s;
}

.login-btn:hover {
  background: #4338ca;
}

.settings-menu {
  position: relative;
  margin-left: auto;
  flex-shrink: 0;
}

.settings-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #ffffff;
  border: 1px solid #e4e8ee;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 180px;
  padding: 4px;
  z-index: 20;
}

.settings-dropdown button,
.settings-dropdown .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  color: #1a1a2e;
  cursor: pointer;
  text-decoration: none;
}

.settings-dropdown button:hover,
.settings-dropdown .dropdown-item:hover {
  background: #f0f2f5;
}

.settings-dropdown svg {
  color: #5f6b7a;
  flex-shrink: 0;
}

/* CONFIG (Domain + Reload) */
.config {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #e4e8ee;
  align-items: center;
}

.filter-input {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #e4e8ee;
  border-radius: 8px;
  font-size: 16px;
  background: #f7f8fa;
  color: #1a1a2e;
  outline: none;
}

.filter-input:focus {
  border-color: #4f46e5;
  background: #ffffff;
}

.reload-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e4e8ee;
  background: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  color: #5f6b7a;
  transition: all .2s;
  flex-shrink: 0;
}

.reload-btn:hover:not(:disabled) {
  background: #4f46e5;
  border-color: #4f46e5;
  color: white;
}

.reload-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

/* CONNECTION LIST */
.connection-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.connection {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background .15s;
}

.connection:hover {
  background: #f7f8fa;
}

.connection.active {
  background: #eef2ff;
}

.conn-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #e0e7ff;
}

.conn-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e0e7ff;
  color: #4f46e5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 15px;
  flex-shrink: 0;
}

.conn-info {
  flex: 1;
  min-width: 0;
}

.conn-name {
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conn-email {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 1px;
}

.conn-number {
  font-size: 10px;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 1px;
}

.badge {
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  flex-shrink: 0;
}

/* CHAT MAIN */
.chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f7f8fa;
}

.chat-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #ffffff;
  border-bottom: 1px solid #e4e8ee;
}

.header-conn-img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #e0e7ff;
}

.header-conn-img.clickable {
  cursor: pointer;
  transition: box-shadow .15s, transform .15s;
}

.header-conn-img.clickable:hover {
  box-shadow: 0 0 0 2px #4f46e5;
}

.chat-header h2 {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.placeholder-title {
  color: #b0b8c9;
}

/* „Alle gelesen"-Knopf im Konversationskopf (nur bei ungelesenen Nachrichten):
   solide rote Pille (= Farbe der Ungelesen-Badges #ef4444), damit sie auffällt */
.mark-all-read {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 30px;
  padding: 0 13px;
  border: 0;
  border-radius: 999px;
  background: #ef4444;
  color: #ffffff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(239, 68, 68, 0.35);
  transition: background .15s, box-shadow .15s;
}
.mark-all-read:hover {
  background: #dc2626;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.45);
}
.mark-all-read svg {
  flex-shrink: 0;
}

/* MESSAGES */
.messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.msg-row {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  align-items: flex-end;
}

.msg-row.me {
  flex-direction: row-reverse;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
}

.avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e0e7ff;
  color: #4f46e5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.bubble {
  position: relative;
  background: linear-gradient(135deg, #e8f0fe 0%, #d4e4fc 40%, #e0ecfa 100%);
  padding: 10px 14px;
  border-radius: 14px 14px 14px 4px;
  max-width: 65%;
  box-shadow: 0 1px 3px rgba(100, 140, 200, 0.12);
  word-break: break-word;
}

/* Own-message action menu (edit / delete) — opened by clicking the message. */
.msg-row.me .bubble { cursor: pointer; }

.msg-menu {
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  background: #fff;
  border: 1px solid #e4e8ee;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.14);
  padding: 4px;
  display: flex;
  flex-direction: column;
  min-width: 130px;
  z-index: 10;
  cursor: default;
}
.msg-menu button {
  border: none;
  background: none;
  text-align: left;
  padding: 8px 12px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  color: #1a1a2e;
  cursor: pointer;
}
.msg-menu button:hover { background: #f0f2f5; }
.msg-menu button.danger { color: #dc2626; }
.msg-menu button.danger:hover { background: #fef2f2; }

.msg-row.me .bubble {
  background: linear-gradient(135deg, #ede4f7 0%, #e2d6f3 40%, #e8dcf5 100%);
  color: #2d2440;
  border-radius: 14px 14px 4px 14px;
}

/* Only partner NEW messages are clickable (acknowledge) — own NEW messages
   just show the status but can't be acknowledged by the sender. */
.msg-row.them.unread {
  cursor: pointer;
}

.msg-row.unread .bubble {
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.25), 0 2px 6px rgba(0, 0, 0, 0.12);
}

.msg-row.unread .time {
  color: #6b7280;
}

.msg-row.me.unread .time {
  color: #8a7ab0;
}

.sender {
  font-size: 12px;
  font-weight: 600;
  color: #4a7cc9;
  margin-bottom: 2px;
}

.msg-row.me .sender {
  color: #7c5caa;
}

.text {
  font-size: 14px;
  line-height: 1.45;
}

.markdown :first-child { margin-top: 0; }
.markdown :last-child { margin-bottom: 0; }
.markdown p { margin: 0.4em 0; }
.markdown h1, .markdown h2, .markdown h3 { margin: 0.5em 0 0.3em; font-size: 1.1em; }
.markdown ul, .markdown ol { margin: 0.3em 0; padding-left: 1.4em; }
.markdown li { margin: 0.15em 0; }
.markdown code {
  background: rgba(0, 0, 0, 0.07);
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 0.9em;
}
.msg-row.me .markdown code {
  background: rgba(0, 0, 0, 0.08);
}
.markdown pre {
  background: rgba(0, 0, 0, 0.07);
  padding: 8px 10px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 0.4em 0;
}
.markdown pre code {
  background: none;
  padding: 0;
}
.markdown blockquote {
  border-left: 3px solid rgba(0, 0, 0, 0.15);
  margin: 0.4em 0;
  padding: 2px 10px;
  color: inherit;
  opacity: 0.85;
}
.markdown a {
  color: #4a7cc9;
  text-decoration: underline;
}
.msg-row.me .markdown a {
  color: #7c5caa;
}
.markdown img {
  max-width: 100%;
  border-radius: 6px;
}
.markdown table {
  border-collapse: collapse;
  margin: 0.4em 0;
  font-size: 0.9em;
}
.markdown th, .markdown td {
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 4px 8px;
}

.time {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  text-align: right;
}

.msg-row.me .time {
  color: #9a85be;
}

/* UNREAD DIVIDER */
.unread-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
  color: #9ca3af;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.unread-divider::before,
.unread-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #d1d5db;
}

/* COMPOSER */
.composer {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #ffffff;
  border-top: 1px solid #e4e8ee;
  align-items: flex-end;
}

.composer textarea {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #e4e8ee;
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  resize: none;
  outline: none;
  background: #f7f8fa;
  transition: border-color .15s;
  line-height: 1.4;
}

.composer textarea:focus {
  border-color: #4f46e5;
  background: #ffffff;
}

.attach-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: #f0f2f5;
  color: #4f46e5;
  border-radius: 12px;
  cursor: pointer;
  transition: background .15s;
  flex-shrink: 0;
}

.attach-btn:hover:not(:disabled) {
  background: #e4e8ee;
}

.attach-btn:disabled {
  color: #c7c9d1;
  cursor: default;
}

.send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: #4f46e5;
  color: white;
  border-radius: 12px;
  cursor: pointer;
  transition: background .15s;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  background: #4338ca;
}

.send-btn:disabled {
  background: #c7c9d1;
  cursor: default;
}

/* EMPTY STATE */
.empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #b0b8c9;
}

.empty p {
  font-size: 15px;
  font-weight: 500;
}

/* Anonymous guest welcome shown in the message area before the first message */
.guest-error {
  margin: 0 16px 16px;
  padding: 12px 16px;
  background: #fff4f2;
  border: 1px solid #f3c6bd;
  border-radius: 10px;
  color: #8a2b17;
  font-size: 14px;
}

.guest-welcome {
  margin: 16px;
  padding: 14px 16px;
  background: #f7f8fa;
  border: 1px solid #e4e8ee;
  border-radius: 12px;
  color: #4b5563;
  font-size: 15px;
  line-height: 1.5;
}

/* Success banner after the email address was verified */
.verified-notice {
  margin: 12px 16px;
  padding: 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.verified-notice h4 {
  margin: 0;
  font-size: 15px;
  color: #166534;
}
.verified-notice p {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #15803d;
}
.verified-notice .primary-btn {
  align-self: flex-start;
  margin-top: 4px;
  padding: 8px 16px;
  background: #16a34a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
}
.verified-notice .primary-btn:hover { background: #15803d; }

.loading-indicator {
  text-align: center;
  padding: 16px;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 500;
}

/* MOBILE */
@media (max-width: 768px) {
  .sidebar {
    width: min(300px, 85vw);
    min-width: auto;
  }

  .sidebar.mobile {
    left: calc(-1 * min(300px, 85vw));
  }

  .bubble {
    max-width: 85%;
  }

  .messages {
    padding: 12px;
  }

  .composer {
    padding: 8px 12px;
    padding-bottom: max(8px, env(safe-area-inset-bottom));
  }

  .chat-header {
    padding-top: max(12px, env(safe-area-inset-top));
  }

  /* Auf dem Handy nur das Icon zeigen (Platz für den Verbindungsnamen lassen) */
  .mark-all-read {
    padding: 0 9px;
  }
  .mark-all-read-label {
    display: none;
  }
}
</style>
